import { createClient, type SupabaseClient } from '@supabase/supabase-js';

import { env } from '@/lib/env';

/**
 * The browser-side Supabase client.
 *
 * Owns session persistence, token refresh and the OAuth redirect handshake. The
 * rest of the app never talks to Supabase Auth directly — it goes through the
 * AuthProvider, which is what keeps this module replaceable in tests.
 *
 * PKCE rather than the implicit flow: the auth code is exchanged for tokens on
 * this origin, so an intercepted redirect URL alone is not enough to steal a
 * session.
 */

let client: SupabaseClient | null = null;

export function getSupabaseClient(): SupabaseClient {
  if (client) return client;

  if (!env.hasSupabase) {
    throw new Error(
      'Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY ' +
        'in frontend/.env.local.',
    );
  }

  client = createClient(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      // Scoped so a second app on the same origin cannot clobber the session.
      storageKey: 'interviewforge.auth',
    },
  });

  return client;
}

/** Test-only: replace or clear the singleton between cases. */
export function __setSupabaseClientForTests(next: SupabaseClient | null): void {
  client = next;
}
